import { getVideoPPIDs, setXandrPPIDs } from '@schibsted/advertory/tracker/es';
import type { Tracker } from '@schibsted/pulse-sdk';
import type { Player } from '@schibsted-svp/web-player';
import type { StreamProvider } from '@vgtv/api-client';
import type { Country, SiteConfig } from '@schibsted/advertory/types';

import { resolveWithTimeout } from '../../../utils/async';
import { getConfigProvider } from '../../../utils/player/player';

import { getPulseTracker } from './getPulseTracker';
import { countryMap } from '../../countryMap';

const DEFAULT_TIMEOUT_IN_MILLISECONDS = 2500;

type PulseConfig = NonNullable<SiteConfig['pulse']>;

/** The subset of advertory's SiteConfig consumed by setXandrPPIDs. */
interface PpidSiteConfig {
  pageContext: Pick<SiteConfig['pageContext'], 'countryCode'>;
  pulse: PulseConfig;
  tcf: NonNullable<SiteConfig['tcf']>;
}

export class PpidWrapper {
  private provider: StreamProvider;

  private country: 'no' | 'se';

  ready: Promise<void>;

  tracker: Tracker | null = null;

  constructor(player: Player) {
    this.provider = getConfigProvider(player);
    this.country = (Object.keys(countryMap).find((k) =>
      countryMap[k].includes(this.provider)
    ) ?? 'no') as Country;

    this.ready = resolveWithTimeout({
      promise: getPulseTracker(player).then(async (tracker) => {
        if (!tracker) {
          return;
        }

        this.tracker = tracker;

        const ppidSiteConfig: PpidSiteConfig = {
          pageContext: {
            countryCode: this.country,
          },
          pulse: {
            clientId: this.provider,
            // pulse-sdk's Tracker legacy track methods are not assignable to advertory's PulseInstance & PulseLegacyExtras
            instance: this.tracker as unknown as PulseConfig['instance'],
          },
          tcf: {
            enabled: true,
            advertisingCMPSignalEnabled: true,
            schibstedConsentToPayEnabled: true,
          },
        };

        // advertory only reads the fields declared in PpidSiteConfig; the remaining required SiteConfig fields are not needed
        await setXandrPPIDs(ppidSiteConfig as SiteConfig);
      }),
      fallback: undefined,
      timeout: DEFAULT_TIMEOUT_IN_MILLISECONDS,
    });
  }

  async getEids(): Promise<string> {
    await this.ready;

    try {
      return await getVideoPPIDs(this.country);
    } catch (error) {
      console.error('Error fetching video PPIDs:', error);
      return '';
    }
  }
}
